import { FC, useEffect, useMemo, useRef, useState, FocusEvent } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'

import { isActivePath } from '~/src/components/SideBarWithSubmenus/helpers'
import { isSidebarItemVisible } from '~/src/navigation/helpers'

import { tw } from '~/lib/utils'
import SidebarLink from '~/src/components/SideBarWithSubmenus/components/SidebarLink'
import SidebarMenuButton from '~/src/components/SideBarWithSubmenus/components/SidebarMenuButton'
import Submenu from '~/src/components/SideBarWithSubmenus/components/Submenu'
import SidebarAction from '~/src/components/SideBarWithSubmenus/components/SidebarAction'
import { useTabTracking } from '~/app/(authenticated)/useTabTracking'

import { UserRole } from '~/__generated_types__/globalTypes'

import { SidebarItem } from '~/src/navigation/types'

type Props = {
  items: Array<SidebarItem>
  flags: Record<string, boolean>
  className?: string
  userRole: UserRole
  onAction?: (actionKey: string) => void
}

type OpenReason = 'mouse' | 'focus' | 'keyboard' | null

const INITIAL_OPEN_DELAY = 120
const SWITCH_OPEN_DELAY = 350
const CLOSE_DELAY = 250

const SideBarWithSubmenus: FC<Props> = ({
  items,
  flags,
  className,
  userRole,
  onAction,
}) => {
  const pathname = usePathname() ?? '/'
  const searchParams = useSearchParams()

  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null)
  const [openReason, setOpenReason] = useState<OpenReason>(null)

  const openTimerRef = useRef<number | null>(null)
  const closeTimerRef = useRef<number | null>(null)
  const menuButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map())

  useTabTracking(pathname.split('/').filter(Boolean).pop() || 'unknown')

  const visibleItems = useMemo(
    () =>
      items.filter(
        (item) => item.kind !== 'action' && isSidebarItemVisible(item, flags, userRole)
      ),
    [items, flags, userRole]
  )

  const actionItems = useMemo(
    () =>
      items.filter(
        (item): item is Extract<SidebarItem, { kind: 'action' }> =>
          item.kind === 'action' && isSidebarItemVisible(item, flags, userRole)
      ),
    [items, flags, userRole]
  )

  useEffect(() => {
    return () => {
      if (openTimerRef.current) {
        window.clearTimeout(openTimerRef.current)
      }

      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current)
      }
    }
  }, [])

  const clearOpenTimer = () => {
    if (openTimerRef.current) {
      window.clearTimeout(openTimerRef.current)
      openTimerRef.current = null
    }
  }

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }

  const openMenu = (key: string, reason: OpenReason) => {
    clearOpenTimer()
    clearCloseTimer()
    setOpenMenuKey(key)
    setOpenReason(reason)
  }

  const scheduleOpen = (key: string) => {
    clearOpenTimer()
    clearCloseTimer()

    const delay = openMenuKey ? SWITCH_OPEN_DELAY : INITIAL_OPEN_DELAY

    openTimerRef.current = window.setTimeout(() => {
      setOpenMenuKey(key)
      setOpenReason('mouse')
      openTimerRef.current = null
    }, delay)
  }

  const closeMenu = () => {
    clearOpenTimer()
    clearCloseTimer()
    setOpenMenuKey(null)
    setOpenReason(null)
  }

  const scheduleClose = () => {
    clearOpenTimer()
    clearCloseTimer()

    closeTimerRef.current = window.setTimeout(() => {
      setOpenMenuKey(null)
      setOpenReason(null)
      closeTimerRef.current = null
    }, CLOSE_DELAY)
  }

  const openMenuItem = useMemo(() => {
    if (!openMenuKey) return undefined

    return visibleItems.find(
      (item): item is Extract<SidebarItem, { kind: 'menu' }> =>
        item.kind === 'menu' && item.key === openMenuKey
    )
  }, [visibleItems, openMenuKey])

  const handleMenuButtonKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    item: Extract<SidebarItem, { kind: 'menu' }>
  ) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      closeMenu()

      return
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()

      if (openMenuKey === item.key) {
        closeMenu()
      } else {
        openMenu(item.key, 'keyboard')
      }

      return
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault()
      openMenu(item.key, 'keyboard')
    }
  }

  const handleBlur = (event: FocusEvent<HTMLElement>) => {
    const nextFocused = event.relatedTarget as Node | null

    if (!event.currentTarget.contains(nextFocused)) {
      closeMenu()
    }
  }

  const query = searchParams?.toString() ?? ''
  const result = query ? `/${pathname.slice(1)}?${query}` : `/${pathname.slice(1)}`

  return (
    <aside
      className={tw(
        'relative h-screen shrink-0 border-gray-400 bg-gray-700 [grid-area:sidebar]',
        className
      )}
    >
      <nav
        aria-label="Primary"
        className="relative mt-auto flex h-full flex-col"
        onBlur={handleBlur}
        onMouseEnter={() => {
          clearOpenTimer()
          clearCloseTimer()
        }}
        onMouseLeave={scheduleClose}
      >
        <ul className="flex flex-1 flex-col justify-center">
          {visibleItems.map((item) => {
            if (item.kind === 'link') {
              const active = isActivePath(result, item.href)

              return (
                <li key={item.key}>
                  <SidebarLink
                    active={active}
                    href={item.href}
                    icon={item.icon}
                    title={item.title}
                    onFocus={closeMenu}
                    onMouseEnter={closeMenu}
                  />
                </li>
              )
            }

            if (item.kind === 'menu') {
              const anyChildActive = item.items.some((child) =>
                isActivePath(pathname, child.href)
              )

              return (
                <li key={item.key}>
                  <SidebarMenuButton
                    active={anyChildActive}
                    icon={item.icon}
                    menuId={`submenu-${item.key}`}
                    open={openMenuKey === item.key}
                    ref={(element) => {
                      if (element) {
                        menuButtonRefs.current.set(item.key, element)
                      } else {
                        menuButtonRefs.current.delete(item.key)
                      }
                    }}
                    title={item.title}
                    onFocus={() => openMenu(item.key, 'focus')}
                    onKeyDown={(event) => handleMenuButtonKeyDown(event, item)}
                    onMouseEnter={() => scheduleOpen(item.key)}
                  />
                </li>
              )
            }

            return null
          })}
        </ul>

        <ul className="flex flex-col pb-2">
          {actionItems.map((item) => {
            return (
              <li key={item.key}>
                <SidebarAction
                  icon={item.icon}
                  title={item.title}
                  onClick={() => onAction?.(item.key)}
                />
              </li>
            )
          })}
        </ul>

        {openMenuItem && (
          <Submenu
            id={`submenu-${openMenuItem.key}`}
            menu={openMenuItem}
            pathname={pathname}
            shouldAutoFocus={openReason === 'keyboard'}
            onCancelClose={() => {
              clearOpenTimer()
              clearCloseTimer()
            }}
            onEscape={() => {
              closeMenu()
              menuButtonRefs.current.get(openMenuItem.key)?.focus()
            }}
            onRequestClose={scheduleClose}
          />
        )}
      </nav>
    </aside>
  )
}

export default SideBarWithSubmenus
